import { Component, inject, input, OnChanges, OnInit, output, Signal, SimpleChanges } from '@angular/core';
import { RoomHistoryCardComponent } from '@shared/component/room-history-card/room-history-card.component';
import { HistoryFilter, Paging, Room, RoomUser } from '@shared/transport.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs';
import { UserService } from '@shared/service/user.service';
import { TranslocoPipe } from '@ngneat/transloco';
import { LoaderComponent } from '@shared/component/loader/loader.component';
import { DateLineComponent } from '@shared/component/ui/date-line/date-line.component';
import { IonContent, IonInfiniteScroll, IonInfiniteScrollContent, IonItem, IonItemOption, IonItemOptions, IonItemSliding, IonLabel, IonList } from '@ionic/angular/standalone';
import { InfiniteScrollCustomEvent } from '@ionic/angular';
import { Store } from '@ngxs/store';
import { UserState } from '@shared/store/user/user-state.service';
import { DialogService } from '@service/dialog.service';

@UntilDestroy()
@Component({
  selector: 'app-room-history',
  standalone: true,
  imports: [
    RoomHistoryCardComponent,
    TranslocoPipe,
    LoaderComponent,
    DateLineComponent,
    IonContent,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonList,
    IonItem,
    IonItemOption,
    IonItemOptions,
    IonItemSliding,
    IonLabel
  ],
  templateUrl: './room-history.component.html',
  styleUrl: './room-history.component.scss'
})
export class RoomHistoryComponent implements OnInit, OnChanges {
  readonly filterChanged = input<boolean>(false);
  readonly filter = input<HistoryFilter>()
  readonly roomArchived = input<Room>();
  readonly roomSelected = output<Room>();

  private readonly _userService = inject(UserService);
  private readonly _store = inject(Store);
  private readonly _dialogService = inject(DialogService);
  protected readonly me: Signal<RoomUser> = this._store.selectSignal(UserState.getUser);

  chats: Room[] = [];
  loading = false;
  upgradeTariff = false;
  private _paging: Paging = {
    currentPage: 1,
    totalPages: 0,
    pageSize: 10,
  };
  protected loadingMore = false;
  protected empty = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterChanged?.currentValue === true) {
      this.ngOnInit();
    }
    if (changes.roomArchived?.currentValue) {
      if (changes.roomArchived.currentValue.lastMessage) {
        this.chats.unshift(changes.roomArchived.currentValue);
      }
    }
  }

  ngOnInit(): void {
    this.loadHistory();
  }

  private loadHistory() {
    this.loading = true;
    this._userService.historyRooms(this._paging.pageSize, (this._paging.currentPage - 1) * this._paging.pageSize, this.filter())
      .pipe(untilDestroyed(this), finalize(() => this.loading = false))
      .subscribe({
        next: res => {
          this.chats = res.list;
          this._paging.totalPages = res.total;
        },
        error: () => {
          this.upgradeTariff = true;
        }
      });
  }

  showRoomHistory(room: Room): void {
    this.roomSelected.emit(room);
  }

  loadMoreData(ev: InfiniteScrollCustomEvent): void {
    if (this.loadingMore || this.empty) {
      return;
    }
    this.loadingMore = true;
    this._paging.currentPage++;
    this._userService.historyRooms(this._paging.pageSize, (this._paging.currentPage - 1) * this._paging.pageSize)
      .pipe(untilDestroyed(this), finalize(() => this.loadingMore = false))
      .subscribe(res => {
        this.chats = this.chats.concat(res.list);
        if (!res.list.length || res.list.length < this._paging.pageSize) {
          this.empty = true;
        }
        ev.target.complete();
      });
  }

  deleteRoom(chat: Room) {
    this._dialogService.deleteArchivedRoom(chat)
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        if (res) {
          const ind = this.chats.findIndex(c => c.roomId === res.roomId);
          if (ind >= 0) {
            this.chats.splice(ind, 1);
          }
        }
      });
  }
}
